/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import Arrow from 'components/Icons/Arrow/Arrow';
import Spinner from 'components/Spinner/Spinner';
import Default from './Default/Default';
import Job from './Job/Job';
import Rent from './Rent/Rent';
import './Step.scss';

/**
 * This is the Step component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Step({
  id,
  actionTitle,
  loading,
  form,
  route,
  onNext,
  onPrev,
  ...props
}) {
  const formNode = useRef(null);

  function onClickNext() {
    if (formNode.current.validate()) {
      onNext();
    }
  }

  function renderPrev() {
    if (!form) return null;
    if (!form.prev) return null;

    return (
      <div className="prev">
        <button type="button" disabled={loading} onClick={onPrev}>
          <span>
            <Arrow left />
            <span>{form.prev}</span>
          </span>
        </button>
      </div>
    );
  }

  function renderActionTitle() {
    if (!actionTitle) return null;

    return <h3 className="h6">{actionTitle}</h3>;
  }

  function renderNext() {
    if (!form) return null;
    if (!form.next) return null;
    if (route === 'media') return null;

    if (typeof form.submit === 'object') {
      form.submit.color = 'maroon';
    }

    return (
      <button
        type="button"
        className="button red"
        disabled={loading}
        onClick={onClickNext}
      >
        {form.next}
      </button>
    );
  }

  function renderSpinner() {
    if (!loading) return null;

    return <Spinner />;
  }

  const actions = (
    <>
      {renderActionTitle()}
      {renderSpinner()}
      {renderPrev()}
      {renderNext()}
    </>
  );

  switch (id) {
    case 'job':
      return (
        <Job
          ref={formNode}
          id={id}
          form={form}
          route={route}
          disabled={loading}
          {...props}
          actions={actions}
        />
      );
    case 'rent':
      return (
        <Rent
          ref={formNode}
          id={id}
          form={form}
          route={route}
          disabled={loading}
          {...props}
          actions={actions}
        />
      );
    default:
      return (
        <Default
          ref={formNode}
          id={id}
          form={form}
          route={route}
          disabled={loading}
          {...props}
          actions={actions}
        />
      );
  }
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Step.propTypes = {
  id: PropTypes.string.isRequired,
  actionTitle: PropTypes.string,
  route: PropTypes.string,
  form: PropTypes.shape({
    prev: PropTypes.string,
    next: PropTypes.string,
    submit: PropTypes.shape({
      color: PropTypes.string,
    }),
  }).isRequired,
  loading: PropTypes.bool,
  onNext: PropTypes.func.isRequired,
  onPrev: PropTypes.func.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Step.defaultProps = {
  id: 'step',
  actionTitle: null,
  form: {},
  loading: false,
  onNext: () => {},
  onPrev: () => {},
};

export default Step;
